import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '@/app/i18n'
import { amountInWords } from '@/shared/lib/amountInWords'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import type { BranchRef, CashTransfer } from './cashdeskApi'
import { RECEIPT_FONT } from './receiptPrint'
import { referenceLabel, transferTypeName, type CashTransferType } from './transferTypeApi'
import { SLIP_HEIGHT_MM, SLIP_WIDTH_MM, TRANSFER_SLIP_PRINT_CSS, TRANSFER_SLIP_PRINT_ROOT } from './transferSlipPrint'

export interface SlipCompany {
  name: string | null
  logoUrl: string | null
}

interface TransferSlipProps {
  transfer: CashTransfer
  types: readonly CashTransferType[] | undefined
  company: SlipCompany
}

const clamp = (lines: number): CSSProperties => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
})

const label: CSSProperties = { fontSize: '7.5pt', textTransform: 'uppercase', letterSpacing: '0.2mm' }
const mono: CSSProperties = { fontFamily: RECEIPT_FONT }
const title: CSSProperties = { fontSize: '14pt', fontWeight: 700, letterSpacing: '0.4mm' }
const amountBox: CSSProperties = {
  border: '0.3mm solid #000',
  padding: '2mm 3mm',
  display: 'flex',
  flexDirection: 'column',
  gap: '1mm',
}

function Field({ name, children, wide }: { name: string; children: ReactNode; wide?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '2mm', gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={{ ...label, flex: '0 0 30mm' }}>{name}</span>
      <span style={{ flex: 1, minWidth: 0, borderBottom: '0.2mm solid #000', paddingBottom: '0.5mm', ...clamp(2) }}>
        {children}
      </span>
    </div>
  )
}

function Branch({ branch }: { branch: BranchRef }) {
  return (
    <>
      {branch.name} (<bdi style={mono}>{branch.code}</bdi>)
    </>
  )
}

function SlipBody({ transfer, types, company }: TransferSlipProps) {
  const { t } = useTranslation()
  const [logoBroken, setLogoBroken] = useState(false)
  const type = types?.find((candidate) => candidate.code === transfer.transferType)

  return (
    <section
      style={{
        boxSizing: 'border-box',
        height: `${SLIP_HEIGHT_MM}mm`,
        padding: '9mm 12mm',
        display: 'flex',
        flexDirection: 'column',
        gap: '3mm',
        overflow: 'hidden',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8mm' }}>
        <div style={{ minWidth: 0 }}>
          {company.logoUrl && !logoBroken ? (
            <img
              src={company.logoUrl}
              alt=""
              onError={() => setLogoBroken(true)}
              style={{ display: 'block', maxHeight: '12mm', maxWidth: '50mm', marginBottom: '1mm' }}
            />
          ) : null}
          {company.name ? <div style={{ fontWeight: 700, fontSize: '11pt', ...clamp(2) }}>{company.name}</div> : null}
        </div>
        <div style={{ textAlign: 'end', maxWidth: '60%' }}>
          <div style={title}>{t('cashdesk.transferSlip.title')}</div>
        </div>
      </header>

      <div style={{ borderTop: '0.4mm solid #000' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '8mm', rowGap: '2.2mm' }}>
        <Field name={t('cashdesk.transferSlip.slipNo')}>
          <bdi style={{ ...mono, fontWeight: 700 }}>{transfer.reference}</bdi>
        </Field>
        <Field name={t('cashdesk.transfer.type')}>{transferTypeName(transfer.transferType, types, t)}</Field>
        <Field name={t('cashdesk.transferSlip.sentAt')}>
          <bdi style={mono}>{formatDateTime(transfer.sentAt)}</bdi>
        </Field>
        <Field name={t('cashdesk.transferSlip.receivedAt')}>
          <bdi style={mono}>{formatDateTime(transfer.decidedAt)}</bdi>
        </Field>
        <Field name={t('cashdesk.transferSlip.fromBranch')}>
          <Branch branch={transfer.fromBranch} />
        </Field>
        <Field name={t('cashdesk.transferSlip.toOffice')}>
          <Branch branch={transfer.toNode} />
        </Field>
        {transfer.externalReference ? (
          <Field name={type ? referenceLabel(type, t) : t('cashdesk.transfer.externalReference')}>
            <bdi style={mono}>{transfer.externalReference}</bdi>
          </Field>
        ) : null}
        <Field name={t(transfer.externalReference ? 'cashdesk.transfer.depositedBy' : 'cashdesk.transfer.responsible')}>
          {transfer.responsiblePerson}
        </Field>
        <Field name={t('cashdesk.transfer.sentBy')}>{transfer.sentBy.name}</Field>
        <Field name={t('cashdesk.transferSlip.confirmedBy')}>{transfer.decidedBy ? transfer.decidedBy.name : t('common.dash')}</Field>
      </div>

      <div style={amountBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '4mm' }}>
          <span style={label}>{t('cashdesk.transferSlip.amount')}</span>
          <bdi style={{ ...mono, fontSize: '14pt', fontWeight: 700 }}>{formatMoneyValue(transfer.amount)}</bdi>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '2mm' }}>
          <span style={{ ...label, flexShrink: 0 }}>{t('cashdesk.transferSlip.amountInWords')}</span>
          <span style={{ fontStyle: 'italic', ...clamp(2) }}>{amountInWords(transfer.amount, currentLanguage())}</span>
        </div>
      </div>

      {transfer.decisionNote ? (
        <Field name={t('cashdesk.transferSlip.confirmationNote')} wide>
          {transfer.decisionNote}
        </Field>
      ) : null}
      {transfer.remarks ? (
        <Field name={t('cashdesk.transfer.remarks')} wide>
          {transfer.remarks}
        </Field>
      ) : null}

      <div style={{ fontSize: '8pt' }}>{t('cashdesk.transferSlip.statement')}</div>

      <footer style={{ marginTop: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: '10mm' }}>
        {[
          t('cashdesk.transferSlip.signSender'),
          t('cashdesk.transferSlip.signCashier'),
          t('cashdesk.transferSlip.signSupervisor'),
        ].map((caption) => (
          <div key={caption} style={{ textAlign: 'center' }}>
            <div style={{ height: '9mm', borderBottom: '0.2mm solid #000' }} />
            <div style={{ ...label, marginTop: '1mm' }}>{caption}</div>
          </div>
        ))}
      </footer>
    </section>
  )
}

export function TransferSlip(props: TransferSlipProps) {
  const { i18n } = useTranslation()
  return (
    <div
      dir={i18n.dir()}
      style={{
        width: `${SLIP_WIDTH_MM}mm`,
        fontFamily: 'Arial, "DejaVu Sans", sans-serif',
        fontSize: '9.5pt',
        lineHeight: 1.3,
        color: '#000',
        background: '#fff',
      }}
    >
      <SlipBody {...props} />
    </div>
  )
}

export function TransferSlipPrintPortal(props: TransferSlipProps) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = TRANSFER_SLIP_PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={TRANSFER_SLIP_PRINT_ROOT}>
      <TransferSlip {...props} />
    </div>,
    document.body,
  )
}
